import React from 'react';
import BankCardFilled from '@monofront/design-system/visuals/Icons/Old/BankCardFilled';
import BankCardOutline from '@monofront/design-system/visuals/Icons/Old/BankCardOutline';
import DeliveryFilled from '@monofront/design-system/visuals/Icons/Old/DeliveryFilled';
import DeliveryOutline from '@monofront/design-system/visuals/Icons/Old/DeliveryOutline';
import UserFilled from '@monofront/design-system/visuals/Icons/Old/UserFilled';
import UserOutline from '@monofront/design-system/visuals/Icons/Old/UserOutline';
import { useRouter } from 'next/router';
import { useSubscribeInCheckout } from '@monofront/business/hooks';
import { useAuthentication } from '@context/businessContainer';

export enum ICheckoutSteps {
  INTERMEDIARY = 'INTERMEDIARY',
  REGISTRATION = 'REGISTRATION',
  DELIVERY = 'DELIVERY',
  PAYMENT = 'PAYMENT',
}

export interface ICheckoutStep {
  currentStep: ICheckoutSteps;
  Link?: React.FC<{ href: string }>;
}

const withStepperIcon =
  (Filled, Outlined) =>
  ({ active, current }) => {
    return (
      <>
        {current ? (
          <Filled height={32} width={32} aria-hidden="true" />
        ) : active ? (
          <Outlined height={32} width={32} aria-hidden="true" />
        ) : (
          <Outlined
            color="var(--clr-grey-4)"
            height={32}
            width={32}
            aria-hidden="true"
          />
        )}
      </>
    );
  };

enum TitleTranslationKeyEnum {
  REGISTRATION = 'registration',
  DELIVERY = 'delivery',
  PAYMENT = 'payment',
}

export interface IStep {
  Icon: any;
  titleTranslationKey: TitleTranslationKeyEnum;
  onClick?: () => void;
  step: ICheckoutSteps;
  dataTestId?: string;
}

export const registrationStep: IStep = {
  Icon: withStepperIcon(UserFilled, UserOutline),
  titleTranslationKey: TitleTranslationKeyEnum.REGISTRATION,
  step: ICheckoutSteps.REGISTRATION,
};

export const getDeliveryStep = ({ onClick }): IStep => ({
  Icon: withStepperIcon(DeliveryFilled, DeliveryOutline),
  titleTranslationKey: TitleTranslationKeyEnum.DELIVERY,
  onClick,
  step: ICheckoutSteps.DELIVERY,
  dataTestId: 'CheckoutStepper-go-to-checkout-delivery',
});

export const paymentStep: IStep = {
  Icon: withStepperIcon(BankCardFilled, BankCardOutline),
  titleTranslationKey: TitleTranslationKeyEnum.PAYMENT,
  step: ICheckoutSteps.PAYMENT,
};

const useCheckoutSteps = (): IStep[] => {
  const { isSubscriptionAvailable, isLoading } =
    useSubscribeInCheckout(useAuthentication);

  const router = useRouter();
  const deliveryStep = getDeliveryStep({
    onClick: () => router.push('/checkout/delivery'),
  });

  if (isLoading) {
    return [];
  }

  return isSubscriptionAvailable
    ? [registrationStep, deliveryStep, paymentStep]
    : [deliveryStep, paymentStep];
};

export default useCheckoutSteps;
